import moment from 'moment';

const push422ErrorsToValidator = (error, validator) => {
    if (error.response && error.response.status === 422 && error.response.data.errors) {
        Object.entries(error.response.data.errors).forEach((item) => {
            const [field, message] = item;
            validator.errors.add({
                field,
                msg: message[0],
            });
        });
    }
};

const capitalizeFirstLetter = (string) => string.charAt(0).toUpperCase() + string.slice(1);

const slugFromString = (string, separator = '-') => {
    let slug = string;
    const sep = separator;

    // Convert all dashes/underscores into separator
    const flip = sep === '-' ? '_' : '-';
    slug = slug.replace(flip, sep);

    // Remove all characters that are not the separator, letters, numbers, or whitespace.
    slug = slug.toLowerCase()
        .replace(new RegExp(`[^a-z0-9${sep}\\s]`, 'g'), '');

    // Replace all separator characters and whitespace by a single separator
    slug = slug.replace(new RegExp(`[${sep}\\s]+`, 'g'), sep);

    return slug.replace(new RegExp(`^[${sep}\\s]+|[${sep}\\s]+$`, 'g'), '');
};

const arrayGenerator = (length) => Array.from({ length }, (_, i) => i + 1);

const isEmptyObject = (obj) => !Object.keys(obj).length;

const getObjectKeyBasedOnValue = (obj, value) => Object.keys(obj).find((key) => obj[key] === value);

const objectToMap = (obj) => {
    const orderedKeys = ['', 1, 0];
    const map = new Map();
    orderedKeys.forEach((key) => {
        if (obj.hasOwnProperty(key)) {
            map.set(key, obj[key]);
        }
    });
    return map;
};

const deleteFromArray = (array, value) => {
    const key = array.indexOf(value);
    if (key !== -1) {
        array.splice(key, 1);
    }
    return array;
};

const objectPick = (obj, keys, nullToEmptyString = true) => Object.keys(obj)
    .filter((i) => keys.includes(i))
    .reduce((acc, key) => {
        acc[key] = (nullToEmptyString && obj[key] === null) ? '' : obj[key];
        return acc;
    }, {});

const objectMap = (obj, fn) => Object.fromEntries(
    Object.entries(obj).map(
        ([k, v], i) => [k, fn(v, k, i)],
    ),
);

const objectFilter = (obj, fn) => Object.keys(obj)
    .filter((key) => fn(obj[key]))
    .reduce((res, key) => {
        res[key] = obj[key];
        return res;
    }, {});

const filterFieldDependency = (obj, dependencyOnField, isBigger) => Object.fromEntries(
    Object.entries(obj)
        .filter(([key]) => {
            const condition = isBigger
                ? parseInt(key, 10) >= parseInt(dependencyOnField, 10)
                : parseInt(key, 10) <= parseInt(dependencyOnField, 10);
            return condition || !dependencyOnField || dependencyOnField.length === 0;
        }),
);

const getDefaultDateRangeValue = () => ({
    startDate: moment().format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD'),
});

const getNullDateRangeValue = () => ({
    startDate: null,
    endDate: null,
});

const parseDateRange = (string) => {
    if (!string) {
        return getNullDateRangeValue();
    }
    const dates = string.split('-');
    if (!dates[1]) {
        return getNullDateRangeValue();
    }
    return {
        startDate: moment(dates[0], 'YYYYMMDD').format('YYYY-MM-DD'),
        endDate: moment(dates[1], 'YYYYMMDD').format('YYYY-MM-DD'),
    };
};

const getDateRanges = (past = false) => {
    const today = moment().toDate();
    if (past) {
        const yesterday = moment().subtract(1, 'days').toDate();
        const lastWeek = moment().subtract(1, 'weeks').toDate();
        const thisMonthStart = moment().startOf('month').toDate();
        const lastMonthStart = moment().subtract(1, 'months').startOf('month').toDate();
        const last2monthsStart = moment().subtract(2, 'months').startOf('month').toDate();
        const last3monthsStart = moment().subtract(3, 'months').startOf('month').toDate();
        const lastYearStart = moment().subtract(1, 'years').startOf('year').toDate();

        return {
            Yesterday: [yesterday, today],
            'Last Week': [lastWeek, today],
            'This Month': [thisMonthStart, today],
            'Last Month': [lastMonthStart, today],
            'Last 2 Months': [last2monthsStart, today],
            'Last 3 Months': [last3monthsStart, today],
            'Last Year': [lastYearStart, today],
        };
    }
    const thisMonthEnd = moment().endOf('month').toDate();
    const nextMonthStart = moment().add(1, 'months').startOf('month').toDate();
    const nextMonthEnd = moment().add(1, 'months').endOf('month').toDate();
    const in2monthsStart = moment().add(2, 'months').startOf('month').toDate();
    const in2monthsEnd = moment().add(2, 'months').endOf('month').toDate();
    const in3monthsStart = moment().add(3, 'months').startOf('month').toDate();
    const in3monthsEnd = moment().add(3, 'months').endOf('month').toDate();
    const thisYearEnd = moment().endOf('year').toDate();
    const nextYearStart = moment().add(1, 'years').startOf('year').toDate();
    const nextYearEnd = moment().add(1, 'years').endOf('year').toDate();

    return {
        'This Month': [today, thisMonthEnd],
        'Next Month': [nextMonthStart, nextMonthEnd],
        'In Two Months': [in2monthsStart, in2monthsEnd],
        'In Three Months': [in3monthsStart, in3monthsEnd],
        'This Year': [today, thisYearEnd],
        'Next Year': [nextYearStart, nextYearEnd],
    };
};

const dateRangeToString = (obj) => `${moment(obj.startDate).format('YYYYMMDD')}-${moment(obj.endDate).format('YYYYMMDD')}`;

const scrollToLastMessage = (bubble, scroller) => {
    if (!scroller || !bubble) {
        return;
    }

    scroller.scrollTo({
        top: bubble.offsetTop,
        left: 0,
        behavior: 'smooth',
    });
};

const handleTwilioUserNameOf = (user) => (user && user.attributes && !user.attributes.is_deleted ? user.friendlyName : 'Deleted Account');

const startOnScrollAnimation = (isVisible, entry, classNames, delay = 0) => {
    if (isVisible) {
        setTimeout(() => {
            classNames.forEach((className) => {
                entry.target.classList.add(className);
            });
        }, delay);
    }
};

const randomString = (length, specialChars = false) => {
    let result = '';
    let characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const upperCaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowerCaseLetters = 'abcdefghijklmnopqrstuvwxyz';

    if (specialChars) {
        characters += '!@#$%^&*()-_=+';
    }
    const charactersLength = characters.length;
    const upperCaseLettersLength = upperCaseLetters.length;
    const lowerCaseLettersLength = lowerCaseLetters.length;

    result += upperCaseLetters.charAt(Math.floor(Math.random() * upperCaseLettersLength));
    result += lowerCaseLetters.charAt(Math.floor(Math.random() * lowerCaseLettersLength));

    let counter = 2;
    while (counter < length) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength));
        counter += 1;
    }
    return result;
};

const getWeeksPassed = (dateString) => {
    const date = moment(dateString, 'DD/MM/YYYY');
    const today = moment();
    const weeksPassed = today.diff(date, 'weeks');

    return weeksPassed;
};
const getStaticImageUrl = (
    address,
    zoom = 15,
    width = 300,
    height = 300,
    scale = 1,
    markerSize = 'mid',
    markerColor,
    markerAdresses,
) => {
    let markersString = '';
    markerAdresses.forEach((marker) => {
        markersString += marker;
        markersString += '%7C';
    });
    markersString = markersString.slice(0, -3);
    return `https://maps.googleapis.com/maps/api/staticmap?center=${address}&zoom=${zoom}&size=${width}x${height}&scale=${scale}&markers=size:${markerSize}%7Ccolor:0x${markerColor}%7C${markersString}&key=${process.env.MIX_GOOGLE_MAPS_API_KEY}`;
};

const changeDateFormat = (date) => {
    if (typeof date === 'string' && date.includes('hours ago')) {
        return date;
    }
    const parsedDate = moment(date, 'DD/MM/YYYY', true);
    if (parsedDate.isValid()) {
        return parsedDate.format('DD MMM YYYY');
    }
    const parsedDate2 = moment(date, 'YYYY-MM-DD', true);
    return parsedDate2.isValid() ? parsedDate2.format('DD MMM YYYY') : undefined;
};

const parsePrice = (price) => `£${price.toLocaleString()}`;

export {
    arrayGenerator,
    capitalizeFirstLetter,
    changeDateFormat,
    dateRangeToString,
    deleteFromArray,
    filterFieldDependency,
    getDateRanges,
    getDefaultDateRangeValue,
    getNullDateRangeValue,
    getObjectKeyBasedOnValue,
    getStaticImageUrl, getWeeksPassed,
    handleTwilioUserNameOf,
    isEmptyObject,
    objectFilter,
    objectMap,
    objectPick,
    objectToMap,
    parseDateRange,
    parsePrice,
    push422ErrorsToValidator,
    randomString,
    scrollToLastMessage,
    slugFromString,
    startOnScrollAnimation
};

